import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Search, ShieldCheck, User as UserIcon, Printer, MoreHorizontal, Pencil, Eye, ChevronDown, Check, Trash2 } from 'lucide-react';
import { adminApi, resolveUrl } from '@/services/adminApi';
import type { AdminUser, AdminUsersResponse } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader, PageContent, EmptyState, LoadingState, Button, Table, StatusBadge, AlertDialog } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';
import { UserRole } from '@printtat/shared';
import { EditUserModal } from '@/components/EditUserModal';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: UserRole.USER, label: 'User' },
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.PRINTER, label: 'Printer' },
];

const ROLE_COLORS: Record<string, 'purple' | 'blue' | 'gray'> = {
  admin: 'purple', printer: 'blue', user: 'gray',
};

const ROLE_ICONS: Record<string, typeof UserIcon> = {
  admin: ShieldCheck, printer: Printer, user: UserIcon,
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const displayName = (u: AdminUser) => {
  if (u.firstName || u.lastName) return [u.firstName, u.lastName].filter(Boolean).join(' ');
  return u.email.split('@')[0];
};

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);

  const limit = 20;

  const { data, isLoading } = useQuery<AdminUsersResponse>({
    queryKey: [...queryKeys.users.all(), { search, role: roleFilter, page, limit, ...sort }],
    queryFn: () => adminApi.users.list({
      search: search || undefined,
      role: roleFilter || undefined,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { firstName?: string; lastName?: string; role?: string; isVerified?: boolean } }) =>
      adminApi.users.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEditingUser(null);
    },
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => adminApi.users.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setDeletingUser(null);
    },
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<AdminUser>[] = [
    {
      key: 'user',
      header: 'User',
      render: (user) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            {user.profilePicture ? (
              <img src={resolveUrl(user.profilePicture)} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <UserIcon size={14} className="text-gray-400" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{displayName(user)}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (user) => {
        const Icon = ROLE_ICONS[user.role] ?? UserIcon;
        return <StatusBadge label={user.role} color={ROLE_COLORS[user.role] ?? 'gray'} icon={<Icon size={12} />} size="xs" />;
      },
    },
    {
      key: 'orderCount',
      header: 'Orders',
      sortable: true,
      render: (user) => <span className="text-sm text-gray-600">{user.orderCount}</span>,
    },
    {
      key: 'createdAt',
      header: 'Joined',
      sortable: true,
      render: (user) => <span className="text-sm text-gray-500">{formatDate(user.createdAt)}</span>,
    },
    {
      key: 'email',
      header: 'Verified',
      sortable: false,
      render: (user) => (
        <span className={`text-xs font-medium ${user.isVerified ? 'text-green-600' : 'text-gray-400'}`}>
          {user.isVerified ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'w-10',
      render: (user) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={16} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/users/${user.id}`)}>
              <Eye size={14} className="mr-2" />
              View details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setEditingUser(user)}>
              <Pencil size={14} className="mr-2" />
              Edit user
            </DropdownMenuItem>
            {currentUser?.id !== user.id && (
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => setDeletingUser(user)}
              >
                <Trash2 size={14} className="mr-2" />
                Delete user
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContent className="overflow-y-auto h-screen">
      <PageHeader
        icon={Users}
        title="Users"
        description={`${total} total user${total !== 1 ? 's' : ''}`}
      />

      {/* Filters */}
      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors">
              {ROLE_OPTIONS.find(o => o.value === roleFilter)?.label ?? 'All roles'}
              <ChevronDown size={14} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {ROLE_OPTIONS.map(opt => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => { setRoleFilter(opt.value); setPage(1); }}
                className="flex items-center justify-between gap-4"
              >
                {opt.label}
                {roleFilter === opt.value && <Check size={14} className="text-gray-500" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Users table */}
      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users found"
            description={search || roleFilter ? 'Try adjusting your filters' : undefined}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={users}
              keyExtractor={(u) => u.id}
              onRowClick={(u) => navigate(`/users/${u.id}`)}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit user modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          open={!!editingUser}
          onOpenChange={(open) => { if (!open) setEditingUser(null); }}
          onSave={(data) => updateUser.mutate({ id: editingUser.id, data })}
          loading={updateUser.isPending}
        />
      )}

      {/* Delete user confirmation */}
      <AlertDialog
        open={!!deletingUser}
        onOpenChange={(open) => { if (!open) setDeletingUser(null); }}
        title="Delete user"
        description={deletingUser ? `Permanently delete ${displayName(deletingUser)} (${deletingUser.email})? Their orders, designs, addresses, and assets will be removed.` : ''}
        confirmText="Delete"
        variant="destructive"
        loading={deleteUser.isPending}
        onConfirm={() => { if (deletingUser) deleteUser.mutate(deletingUser.id); }}
        onCancel={() => setDeletingUser(null)}
      />
    </PageContent>
  );
};
